import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import personIcon from "../../src/assests/personIcon.svg";
import { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../providers/AuthProvider";
import BrandedLogo from "./branded-logo";
import SideBarData from "../utils/data/sideBar";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import clsx from "clsx";
import ThemeToggle from "./theme-toggle";
import {
  faAngleLeft,
  faRightFromBracket,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useIsMobile, useSidebar } from "../store/use-sidebar";

export default function LeftSidebar() {
  const { user, HandleUserLogOut } = useContext(AuthContext);
  const {
    collapsed: storedCollapsed,
    toggleCollapsed,
    setMobileOpen,
  } = useSidebar();
  const isMobile = useIsMobile();
  // The mobile drawer is always shown expanded; collapse is a desktop preference.
  const collapsed = storedCollapsed && !isMobile;
  const closeMobile = () => isMobile && setMobileOpen(false);

  // Growing past the breakpoint with the drawer open must not leave it stuck open.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile, setMobileOpen]);

  const isActive = (path: string) => {
    return window.location.pathname === path;
  };

  return (
    /* Sticky on the aside itself, not the wrapper: the wrapper is a flex child that
       stretches to the full page height, and `position: sticky` on a box already as
       tall as the document has nothing to stick within — the rail just scrolled away.
       Pinned at h-screen it stays put against both the top and bottom of the viewport. */
    <aside className="group/sidebar sticky top-0 h-screen bg-surface border-r border-edge animate-fade-in-slow flex flex-col">
      {/* ---------- Brand + collapse ---------- */}
      <div
        className={clsx(
          "flex items-center border-b border-edge-faint transition-all duration-300 shrink-0",
          collapsed ? "justify-center px-2 pt-5 pb-4" : "justify-between px-4 pt-5 pb-4"
        )}
      >
        {!collapsed && (
          <div className="flex items-center overflow-hidden transition-all duration-500 ease-out w-full opacity-100 translate-x-0">
            <BrandedLogo className="w-[104px] shrink-0 opacity-95" />
          </div>
        )}

        <button
          type="button"
          onClick={isMobile ? () => setMobileOpen(false) : toggleCollapsed}
          aria-label={isMobile ? "Close menu" : "Toggle sidebar"}
          title={
            isMobile
              ? "Close menu"
              : collapsed
              ? "Expand sidebar"
              : "Collapse sidebar"
          }
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-edge bg-overlay text-light-gray hover:text-text-primary hover:border-indigo-400/40 hover:shadow-glow-sm transition-all duration-200 cursor-pointer"
        >
          <FontAwesomeIcon
            icon={isMobile ? faXmark : faAngleLeft}
            className={clsx(
              "text-xs transition-transform duration-300",
              collapsed && "rotate-180"
            )}
          />
        </button>
      </div>

      {/* ---------- Navigation ---------- */}
      <ul className="font-medium flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-2">
        {SideBarData.map((item) => {
          const active = isActive(item.route);
          return (
            <div
              className={item.label === "My Customers" ? "mt-6" : ""}
              key={item.route}
            >
              <li className="group relative">
                <Link
                  to={item.route}
                  onClick={closeMobile}
                  title={collapsed ? item.label : undefined}
                  className={clsx(
                    "relative flex items-center transition-colors duration-200 ease-out gap-3.5 py-3",
                    collapsed ? "px-0 justify-center" : "px-6",
                    active
                      ? "bg-active-background text-blue"
                      : "text-light-gray hover:bg-active-background/50 hover:text-text-primary"
                  )}
                >
                  {/* Right-edge pill rather than a full-height border — it marks the
                      active row without adding a hard rule down the panel. */}
                  {active && (
                    <span className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-l-full bg-blue" />
                  )}

                  <FontAwesomeIcon
                    icon={item.icon as IconProp}
                    className="w-6 shrink-0 text-center text-[17px]"
                  />

                  {!collapsed && (
                    <span
                      className={clsx(
                        "flex-1 text-[15px] whitespace-nowrap overflow-hidden inline",
                        active ? "font-semibold" : "font-medium"
                      )}
                    >
                      {item.label}
                    </span>
                  )}
                </Link>

                {/* Collapsed labels move to a hover flyout — an icon rail with no names
                    is unusable for anyone who hasn't memorised the order. */}
                {collapsed && (
                  <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 rounded-lg bg-surface-elevated border border-edge px-3 py-1.5 text-[13px] font-medium text-text-primary shadow-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    {item.label}
                  </span>
                )}
              </li>

              {item?.subData?.length && !collapsed ? (
                <ul className="ml-14 flex flex-col">
                  {item.subData.map((subItem) => (
                    <Link
                      key={subItem.route}
                      to={subItem.route}
                      onClick={closeMobile}
                      className={clsx(
                        "my-3 cursor-pointer w-full font-medium text-nowrap text-[14px] text-light-gray hover:text-text-primary transition-colors duration-200",
                        isActive(subItem.route) && "!text-blue"
                      )}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </ul>

      {/* ---------- Theme + account ---------- */}
      <div className="shrink-0">
        <ThemeToggle collapsed={collapsed} />

        <button
          type="button"
          onClick={HandleUserLogOut}
          title="Log out"
          className="relative w-full text-left border-t border-edge bg-surface-elevated cursor-pointer transition-colors duration-300 hover:bg-active-background"
        >
          <div
            className={clsx(
              "flex items-center gap-3 py-3",
              collapsed ? "px-2 justify-center" : "px-4"
            )}
          >
            <img
              className="w-10 h-10 rounded-full ring-2 ring-indigo-500/30 shrink-0"
              src={personIcon}
              alt=""
            />

            {!collapsed && (
              <>
                <div className="flex flex-col min-w-0 flex-1">
                  <p className="font-medium text-[14px] text-heading-color truncate">
                    {user?.name}
                  </p>
                  <p className="font-medium text-xs text-light-gray capitalize truncate">
                    {user?.type?.replace("-", " ")}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faRightFromBracket}
                  className="text-light-gray text-sm opacity-70"
                />
              </>
            )}
          </div>
        </button>
      </div>
    </aside>
  );
}
